import React, { useCallback, useEffect, useState } from 'react'
import {
  PaginationControls,
  PaginationSelectOption,
} from '../../../composites/GamesOverview/components/PaginationControls'
import { SingleValue } from 'react-select'
import genericTable from './GenericTable.module.css'
import { HeaderSorter } from './ReportCommon'
import { DateTime } from '../../../types/commonTypes'
import classNames from 'classnames'
import { getIcon } from '../../../common/components/icons/utils'
import { ReportSortDir } from '../../../contexts/ReportsContextProvider'

export type CellElement = string | number | DateTime | JSX.Element | null
export type TableRow = CellElement[]
export const NO_POINTS_SKIP = 'NO_POINTS_SKIP'

interface TableProps {
  isSortable?: boolean
  isSelectable?: boolean
  hasPagination?: boolean
  headerLabels: string[]
  headerSortTags?: string[]
  headerWidths: string[]
  activeSortCol?: string
  activeSortDir?: string
  onSort?: (sortKey: string) => (direction: string) => void
  noSortIndexes?: boolean[]
  onToggleRowSelected?: (index: number) => void
  selectedRows?: number[]
  customClassRows?: number[]
  customClassRowClass?: string
  rows: TableRow[]
  // All of below must be provided if hasPagination
  currentPage?: number
  pageSize?: number
  lastPage?: number
  allPageOptions?: PaginationSelectOption[]
  loading?: boolean
  onChangeCurrentPage?: (option: SingleValue<PaginationSelectOption>) => void
  onChangePageSize?: (option: SingleValue<PaginationSelectOption>) => void
  onClickNext?: () => void
  onClickPrev?: () => void
  pageSizeOptions?: PaginationSelectOption[]
  itemsPerPageLabel?: string
  tableTitle?: string
  subTitleRow1?: string
  subTitleRow2?: string
  customClassName?: string
  limitRowsSelected?: boolean
  removeHorizontalMargins?: boolean
  tinyTable?: boolean
}

// *********************************************************************************************************
// NOTE, If rows are selectable, first element of each tablerow CellElement array must be an ID of the row.
// i.e. selectable rows must have one more CellElement than those visible
// header* arrays do not need to containt anything for the leftmost selection column.
// i.e. If selectable then header arrays contain one item less than the rows
// isSortable, isSelectable and hasPagination are not used to make related params mandatory
// --> Can be split to sub-interfaces later with more detailed typings
// --> For now it is up to the user of this component to ensure to provide all related attributes
// *********************************************************************************************************

export const Table: React.FC<TableProps> = ({
  isSortable,
  isSelectable,
  hasPagination,
  headerLabels,
  headerSortTags,
  headerWidths,
  activeSortCol,
  activeSortDir,
  onSort,
  noSortIndexes,
  onToggleRowSelected,
  selectedRows,
  customClassRows,
  customClassRowClass,
  rows,
  currentPage,
  pageSize,
  lastPage,
  allPageOptions,
  loading,
  onChangeCurrentPage,
  onChangePageSize,
  onClickNext,
  onClickPrev,
  pageSizeOptions,
  itemsPerPageLabel,
  tableTitle,
  subTitleRow1,
  subTitleRow2,
  customClassName,
  limitRowsSelected = true,
  removeHorizontalMargins = true,
  tinyTable = false,
}) => {
  const [allRowsSelected, setAllRowsSelected] = useState(false)

  useEffect(() => {
    const allSelected = !rows.some((row) => !selectedRows?.includes(row[0] as number))
    setAllRowsSelected(allSelected)
  }, [rows, selectedRows])

  const toggleSelected = useCallback(
    (id: number) => () => {
      if (onToggleRowSelected) onToggleRowSelected(id)
    },
    [onToggleRowSelected],
  )

  const handleSelectAll = useCallback(() => {
    const temprows = [...rows]
    temprows.forEach((row) => {
      if (allRowsSelected && selectedRows?.includes(row[0] as number) && onToggleRowSelected) {
        onToggleRowSelected(row[0] as number)
      } else if (!selectedRows?.includes(row[0] as number) && onToggleRowSelected) {
        onToggleRowSelected(row[0] as number)
      }
    })
  }, [allRowsSelected, onToggleRowSelected, rows, selectedRows])

  return (
    <div
      className={classNames(
        genericTable.genericTable,
        !isSortable && genericTable.noSort,
        !hasPagination && genericTable.noPagination,
        customClassName && genericTable.customClassContainer,
      )}
    >
      {tableTitle && <div className={genericTable.tableTitle}>{tableTitle}</div>}
      {subTitleRow1 && <div className={genericTable.tableSubTitle}>{subTitleRow1}</div>}
      {subTitleRow2 && <div className={genericTable.tableSubTitle}>{subTitleRow2}</div>}
      <table className={classNames(genericTable.tableWidth, tableTitle && genericTable.topPadding, customClassName)}>
        <colgroup>
          {isSelectable && selectedRows && limitRowsSelected && <col style={{ width: '1%', maxWidth: '16px' }} />}
          {isSelectable && selectedRows && !limitRowsSelected && <col style={{ width: '32px', maxWidth: '32px' }} />}
          {headerWidths
            .filter((item) => item !== NO_POINTS_SKIP)
            .map((headerWidth, index) => {
              return <col key={index} style={{ width: headerWidth }} />
            })}
        </colgroup>
        <thead>
          <tr className={genericTable.theadTr}>
            {isSelectable && selectedRows && !limitRowsSelected && (
              <td key={'select-all'} style={{ maxWidth: '32px', paddingLeft: '0px', border: 'none' }}>
                <div className={classNames(genericTable.checkbox)} onClick={handleSelectAll}>
                  {getIcon(allRowsSelected ? 'radioCheckedIcon' : 'radioIcon')}
                </div>
              </td>
            )}
            {isSelectable && limitRowsSelected && <th style={{ maxWidth: '16px' }} />}
            {headerLabels
              .filter((item) => item !== NO_POINTS_SKIP)
              .map((header, index) => {
                return (
                  <>
                    {isSortable &&
                    onSort &&
                    headerSortTags &&
                    (!noSortIndexes || (noSortIndexes && !noSortIndexes[index])) ? (
                      <HeaderSorter
                        key={index}
                        label={header}
                        isActive={activeSortCol === headerSortTags[index]}
                        onSort={onSort(headerSortTags[index])}
                        activeSortDir={activeSortDir as ReportSortDir}
                      />
                    ) : (
                      <HeaderSorter label={header} key={index} />
                    )}
                  </>
                )
              })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => {
            return (
              <tr key={rowIndex} className={classNames(customClassRows?.includes(rowIndex) && customClassRowClass)}>
                {isSelectable && selectedRows && (
                  <td key={rowIndex} style={{ maxWidth: '16px', paddingLeft: '0px' }}>
                    <div
                      className={classNames(
                        genericTable.checkbox,
                        (limitRowsSelected ? selectedRows.length > 2 : false) &&
                          !selectedRows.includes(row[0] as number) &&
                          genericTable.dimmed,
                      )}
                      onClick={toggleSelected(row[0] as number)}
                    >
                      {getIcon(selectedRows.includes(row[0] as number) ? 'radioCheckedIcon' : 'radioIcon')}
                    </div>
                  </td>
                )}
                {row
                  .slice(isSelectable ? 1 : 0)
                  .filter((item) => item !== NO_POINTS_SKIP)
                  .map((cell, cellIndex) => {
                    return (
                      <td
                        key={cellIndex}
                        style={
                          isSortable && headerSortTags && activeSortCol === headerSortTags[cellIndex]
                            ? { fontWeight: 'bold' }
                            : {}
                        }
                      >
                        {cell}
                      </td>
                    )
                  })}
              </tr>
            )
          })}
        </tbody>
      </table>
      {hasPagination && (
        <div className={genericTable.genericTablePagination}>
          <PaginationControls
            currentPage={currentPage!}
            pageSize={pageSize!}
            lastPage={lastPage!}
            allPageOptions={allPageOptions!}
            onClickNext={onClickNext!}
            onClickPrev={onClickPrev!}
            onChangePageSize={onChangePageSize!}
            onChangeCurrentPage={onChangeCurrentPage!}
            loading={loading!}
            pageSizeOptions={pageSizeOptions}
            itemsPerPageLabel={itemsPerPageLabel}
            removeHorizontalMargins={removeHorizontalMargins}
            tinyTable={tinyTable}
          />
        </div>
      )}
    </div>
  )
}
